import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fonction utilitaire pour géocoder une adresse
const geocodeAddress = async (address) => {
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`);
    const data = await response.json();
    
    if (data && data.length > 0) {
      return {
        latitude: parseFloat(data[0].lat),
        longitude: parseFloat(data[0].lon),
        display_name: data[0].display_name
      };
    }
    
    throw new Error('Adresse non trouvée');
  } catch (error) {
    console.error('Erreur géocodage:', error);
    throw error;
  }
};

// Context pour l'authentification
const AuthContext = createContext();

// Hook pour utiliser le contexte d'authentification
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider d'authentification
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, [token]);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur de connexion' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  // NOUVEAU: Gérer l'onglet par défaut selon le rôle
  const getDefaultTab = () => {
    if (user?.role === 'employee') {
      return 'dashboard'; // Les employés commencent sur leur dashboard limité
    }
    return 'dashboard'; // Les admins aussi commencent sur dashboard
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Composant Logo dynamique avec configuration entreprise
const Logo = ({ className = "", size = "normal" }) => {
  const [companyConfig, setCompanyConfig] = useState(null);
  const logoSize = size === "small" ? "50" : "70";
  const textSize = size === "small" ? "text-xl md:text-2xl" : "text-3xl md:text-4xl";
  const subtextSize = size === "small" ? "text-base md:text-lg" : "text-xl md:text-2xl";
  
  useEffect(() => {
    const loadCompanyConfig = async () => {
      try {
        const response = await axios.get(`${API}/company-config`);
        setCompanyConfig(response.data);
      } catch (error) {
        console.error('Error loading company config:', error);
        // Configuration par défaut si erreur
        setCompanyConfig({
          company_name: "ROUCH POINTAGE",
          logo_base64: null
        });
      }
    };
    loadCompanyConfig();
  }, []);
  
  // Affichage par défaut pendant le chargement
  if (!companyConfig) {
    return (
      <div className={`flex items-center space-x-5 ${className}`}>
        <div className="animate-pulse w-16 h-16 bg-gray-300 rounded-full"></div>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-32 mb-2"></div>
          <div className="h-6 bg-gray-300 rounded w-24"></div>
        </div>
      </div>
    );
  }

  // Diviser le nom de l'entreprise en mots pour affichage
  const companyWords = companyConfig.company_name.split(' ');
  const firstWord = companyWords[0] || 'ROUCH';
  const secondWord = companyWords.slice(1).join(' ') || 'POINTAGE';
  
  return (
    <div className={`flex items-center space-x-5 ${className}`}>
      <div className="relative">
        {companyConfig.logo_base64 ? (
          // Logo personnalisé
          <img 
            src={companyConfig.logo_base64} 
            alt="Logo entreprise" 
            className={`drop-shadow-2xl rounded-full object-cover border-2 border-yellow-400`}
            width={logoSize} 
            height={logoSize}
            style={{ width: logoSize + 'px', height: logoSize + 'px' }}
          />
        ) : (
          // Logo par défaut (horloge)
          <svg width={logoSize} height={logoSize} viewBox="0 0 120 120" className="drop-shadow-2xl">
            <defs>
              {/* Gradient principal doré premium */}
              <linearGradient id="premiumGold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F7DC6F" />
                <stop offset="25%" stopColor="#F4D03F" />
                <stop offset="50%" stopColor="#F1C40F" />
                <stop offset="75%" stopColor="#D4AC0D" />
                <stop offset="100%" stopColor="#B7950B" />
              </linearGradient>
              
              {/* Gradient métallique */}
              <linearGradient id="metalGold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FEF9E7" />
                <stop offset="30%" stopColor="#FCF3CF" />
                <stop offset="70%" stopColor="#F8C471" />
                <stop offset="100%" stopColor="#E67E22" />
              </linearGradient>

              {/* Gradient de fond */}
              <radialGradient id="bgGold" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FEF9E7" />
                <stop offset="100%" stopColor="#F4D03F" />
              </radialGradient>

              {/* Filtre ombre */}
              <filter id="modernShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#B7950B" floodOpacity="0.4"/>
              </filter>

              {/* Filtre lueur */}
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#F1C40F" floodOpacity="0.6"/>
              </filter>
            </defs>
            
            {/* Cercle de fond avec dégradé */}
            <circle cx="60" cy="60" r="55" fill="url(#bgGold)" stroke="url(#premiumGold)" strokeWidth="4" filter="url(#modernShadow)"/>
            
            {/* Anneau décoratif externe */}
            <circle cx="60" cy="60" r="48" fill="none" stroke="url(#metalGold)" strokeWidth="2" opacity="0.8"/>
            
            {/* Base de l'horloge - hexagone moderne */}
            <polygon 
              points="60,25 78,35 78,55 60,65 42,55 42,35" 
              fill="#FFFFFF" 
              stroke="url(#premiumGold)" 
              strokeWidth="3" 
              opacity="0.95"
              filter="url(#glow)"
            />
            
            {/* Aiguilles de l'horloge - design moderne */}
            <line x1="60" y1="45" x2="60" y2="32" stroke="url(#premiumGold)" strokeWidth="4" strokeLinecap="round"/>
            <line x1="60" y1="45" x2="70" y2="45" stroke="url(#premiumGold)" strokeWidth="3" strokeLinecap="round"/>
            
            {/* Points de marquage modernes */}
            <circle cx="60" cy="25" r="3" fill="url(#premiumGold)"/>
            <circle cx="85" cy="45" r="3" fill="url(#premiumGold)"/>
            <circle cx="60" cy="65" r="3" fill="url(#premiumGold)"/>
            <circle cx="35" cy="45" r="3" fill="url(#premiumGold)"/>
            
            {/* Centre de l'horloge */}
            <circle cx="60" cy="45" r="5" fill="url(#premiumGold)" filter="url(#glow)"/>
            
            {/* Éléments décoratifs modernes */}
            <path d="M 30 75 Q 60 85 90 75" stroke="url(#metalGold)" strokeWidth="2" fill="none" opacity="0.7"/>
            <path d="M 35 85 Q 60 95 85 85" stroke="url(#metalGold)" strokeWidth="1.5" fill="none" opacity="0.5"/>
            
            {/* Texte moderne en bas */}
            <text x="60" y="105" textAnchor="middle" fontSize="14" fill="url(#premiumGold)" fontWeight="bold" fontFamily="Arial, sans-serif">
              {firstWord.charAt(0)}•{secondWord.charAt(0)}
            </text>
          </svg>
        )}
      </div>
      <div className="flex flex-col">
        <h1 className={`${textSize} font-bold tracking-wider font-sans text-gradient-modern leading-tight`}>
          {firstWord}
        </h1>
        <h2 className={`${subtextSize} font-semibold -mt-2 tracking-wide text-gradient-modern`}>
          {secondWord}
        </h2>
        <div className="text-sm text-gray-500 -mt-1 font-light tracking-wider">
          SYSTÈME RH AVANCÉ
        </div>
      </div>
    </div>
  );
};

// Page de connexion
const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(username, password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <Logo className="justify-center mb-4" />
            <p className="text-white/80 text-sm">Système de gestion des pointages</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-white/90 text-sm font-medium mb-2">
                Nom d'utilisateur
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                placeholder="Entrez votre nom d'utilisateur"
                required
              />
            </div>

            <div>
              <label className="block text-white/90 text-sm font-medium mb-2">
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
                placeholder="Entrez votre mot de passe"
                required
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/60 text-xs">
              Système de pointage professionnel - Sécurisé et fiable
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dashboard principal
const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState({});
  const { user, logout } = useAuth();

  useEffect(() => {
    loadDashboardData();
    loadCurrentTime();
    const interval = setInterval(() => {
      loadDashboardData();
      loadCurrentTime();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadCurrentTime = async () => {
    try {
      const response = await axios.get(`${API}/current-time`);
      setCurrentTime(response.data);
    } catch (error) {
      console.error('Error loading current time:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [statsResponse, employeesResponse] = await Promise.all([
        axios.get(`${API}/dashboard/stats?summary=true`),
        axios.get(`${API}/employees`)
      ]);
      setStats(statsResponse.data);
      setEmployees(employeesResponse.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour filtrer les onglets selon le rôle
  const getAvailableTabs = () => {
    const allTabs = [
      { id: 'dashboard', label: 'Tableau de bord', icon: '📊', roles: ['admin', 'employee'] },
      { id: 'pointage', label: 'Pointage', icon: '⏰', roles: ['admin'] }, // Seulement admin
      { id: 'employees', label: 'Employés', icon: '👥', roles: ['admin'] }, // Seulement admin
      { id: 'calendar', label: 'Calendrier', icon: '📅', roles: ['admin'] }, // Seulement admin
      { id: 'notifications', label: 'Notifications', icon: '📧', roles: ['admin'] }, // Seulement admin
      { id: 'reports', label: 'Rapports', icon: '📈', roles: ['admin'] }, // Seulement admin
      { id: 'config', label: 'Configuration', icon: '⚙️', roles: ['admin'] }, // Seulement admin
      { id: 'my-data', label: 'Mes Données', icon: '👤', roles: ['employee'] } // Seulement employé
    ];

    const userRole = user?.role || 'admin';
    return allTabs.filter(tab => tab.roles.includes(userRole));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header avec barre d'onglets agrandie */}
      <header className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 shadow-2xl border-b-4 border-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24 py-4">
            <div className="flex items-center">
              <Logo size="small" />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              {/* Affichage date/heure actuelle */}
              <div className="text-center">
                <div className="text-white font-semibold">
                  {currentTime.formatted_date || 'Chargement...'}
                </div>
                <div className="text-purple-200 text-sm">
                  {currentTime.formatted_time || '--:--:--'}
                </div>
              </div>
              
              <div className="text-right mr-6">
                <p className="text-lg font-semibold text-white">{user?.nom}</p>
                <p className="text-sm text-purple-200">{user?.role}</p>
              </div>
              <button
                onClick={logout}
                className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-xl text-sm font-medium hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105 shadow-lg"
              >
                🚪 Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Barre de navigation responsive avec fond violet */}
      <nav className="bg-gradient-to-r from-purple-800 via-purple-700 to-purple-800 shadow-lg border-b border-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Navigation desktop */}
          <div className="hidden md:flex space-x-0">
            {getAvailableTabs().map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-3 px-8 py-5 text-base font-medium transition-all duration-200 border-b-4 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-b from-yellow-400 to-orange-500 text-white border-yellow-300 shadow-lg transform scale-105'
                    : 'text-purple-100 hover:text-white hover:bg-purple-600 border-transparent hover:border-purple-400'
                }`}
              >
                <span className="text-xl">{tab.icon}</span>
                <span className="font-semibold">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Navigation mobile */}
          <div className="md:hidden">
            <div className="flex overflow-x-auto scrollbar-hide py-2">
              {getAvailableTabs().map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-col items-center justify-center min-w-[80px] px-3 py-3 mx-1 rounded-lg text-xs font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-b from-yellow-400 to-orange-500 text-white shadow-lg'
                      : 'text-purple-100 hover:text-white hover:bg-purple-600'
                  }`}
                >
                  <span className="text-lg mb-1">{tab.icon}</span>
                  <span className="whitespace-nowrap">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content avec fond cohérent */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gray-50 min-h-screen">
        {activeTab === 'dashboard' && <DashboardTab stats={stats} employees={employees} currentTime={currentTime} onRefresh={loadDashboardData} user={user} />}
        {activeTab === 'pointage' && user?.role === 'admin' && <PointageTab employees={employees} onRefresh={loadDashboardData} />}
        {activeTab === 'employees' && user?.role === 'admin' && <EmployeesTab employees={employees} onRefresh={loadDashboardData} />}
        {activeTab === 'calendar' && user?.role === 'admin' && <CalendarTab employees={employees} />}
        {activeTab === 'notifications' && user?.role === 'admin' && <NotificationsTab employees={employees} />}
        {activeTab === 'reports' && user?.role === 'admin' && <ReportsTab />}
        {activeTab === 'config' && user?.role === 'admin' && <ConfigurationTab />}
        {activeTab === 'my-data' && user?.role === 'employee' && <EmployeeDataTab user={user} />}
      </main>
    </div>
  );
};

// Onglet Tableau de bord avec calendrier mensuel
const DashboardTab = ({ stats, employees, currentTime, onRefresh, user }) => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }); // Format: "2025-07"
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [manualEntry, setManualEntry] = useState({
    employee_id: '',
    type_pointage: 'arrivee',
    timestamp: new Date().toISOString().slice(0, 16),
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMonthlyData();
    // Charger les pointages récents uniquement pour les admins
    if (user?.role === 'admin') {
      loadRecentEntries();
    }
  }, [selectedMonth, user]);

  const loadRecentEntries = async () => {
    try {
      // Limiter à 10 entrées pour l'affichage et améliorer les performances
      const response = await axios.get(`${API}/time-entries?limit=10&offset=0`);
      setRecentEntries(response.data);
    } catch (error) {
      console.error('Error loading recent entries:', error);
      // Définir un tableau vide en cas d'erreur pour éviter les crashes
      setRecentEntries([]);
    }
  };

  const loadMonthlyData = async () => {
    setLoading(true);
    try {
      const [statsResponse, calendarResponse] = await Promise.all([
        axios.get(`${API}/dashboard/stats?month=${selectedMonth}`),
        axios.get(`${API}/calendar/${selectedMonth}`)
      ]);
      setMonthlyStats(statsResponse.data);
      setCalendarData(calendarResponse.data);
    } catch (error) {
      console.error('Error loading monthly data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry({
      ...entry,
      timestamp: new Date(entry.timestamp).toISOString().slice(0, 16) // Format for datetime-local
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/time-entries/${editingEntry.id}`, {
        employee_id: editingEntry.employee_id,
        type_pointage: editingEntry.type_pointage,
        localisation: editingEntry.localisation,
        photo_url: editingEntry.photo_url,
        notes: editingEntry.notes,
        site_nom: editingEntry.site_nom
      });
      alert('Pointage modifié avec succès !');
      setShowEditModal(false);
      setEditingEntry(null);
      loadMonthlyData();
      loadRecentEntries();
      onRefresh();
    } catch (error) {
      console.error('Error updating entry:', error);
      alert('Erreur lors de la modification');
    }
  };

  const handleSaveManual = async () => {
    try {
      await axios.post(`${API}/time-entries/manual`, {
        employee_id: manualEntry.employee_id,
        type_pointage: manualEntry.type_pointage,
        timestamp: new Date(manualEntry.timestamp).toISOString(),
        notes: manualEntry.notes || null
      });
      alert('Pointage manuel ajouté avec succès !');
      setShowManualModal(false);
      setManualEntry({
        employee_id: '',
        type_pointage: 'arrivee',
        timestamp: new Date().toISOString().slice(0, 16),
        notes: ''
      });
      loadMonthlyData();
      loadRecentEntries();
      onRefresh();
    } catch (error) {
      console.error('Error creating manual entry:', error);
      alert('Erreur lors de l\'ajout du pointage manuel');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce pointage ?')) {
      try {
        await axios.delete(`${API}/time-entries/${entryId}`);
        alert('Pointage supprimé avec succès !');
        loadMonthlyData();
        loadRecentEntries();
      } catch (error) {
        console.error('Error deleting entry:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    // Generate options for last 12 months and next 3 months
    for (let i = -12; i <= 3; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // Fix: month + 1
      const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    
    return options;
  };

  // Filtrage des données selon le rôle
  const isEmployee = user?.role === 'employee';
  const displayStats = isEmployee ? {
    total_employees: 1,
    present_employees: user?.statut === 'Présent' ? 1 : 0,
    absent_employees: user?.statut === 'Absent' ? 1 : 0,
    pause_employees: user?.statut === 'En pause' ? 1 : 0,
    presence_rate: user?.statut === 'Présent' ? 100 : 0
  } : stats;

  // Filtrage des employés pour les employés (seulement eux-mêmes)
  const displayEmployees = isEmployee ? 
    employees.filter(emp => emp.id === user?.employee_id) : 
    employees;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mr-3"></div>
        <span>Chargement des données mensuelles...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          {isEmployee ? 'Mon Tableau de Bord' : 'Tableau de bord'}
        </h2>
        
        {/* Sélecteur de mois - seulement pour admin */}
        {!isEmployee && (
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Mois :</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
            >
              {generateMonthOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="text-sm text-gray-500">
              Dernière mise à jour: {new Date().toLocaleTimeString()}
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards avec données mensuelles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">
                {isEmployee ? 'Mon statut' : 'Total Employés'}
              </p>
              <p className="text-2xl font-bold text-gray-900">{displayStats.total_employees || 0}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <span className="text-2xl">👥</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">
                {isEmployee ? 'Présent' : 'Présents'}
              </p>
              <p className="text-2xl font-bold text-green-600">{displayStats.present_employees || 0}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <span className="text-2xl">✅</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">En pause</p>
              <p className="text-2xl font-bold text-orange-600">{displayStats.pause_employees || 0}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <span className="text-2xl">⏸️</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">
                {isEmployee ? 'Absent' : 'Absents'}
              </p>
              <p className="text-2xl font-bold text-red-600">{displayStats.absent_employees || 0}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <span className="text-2xl">❌</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">
                {isEmployee ? 'Mon taux' : 'Taux présence'}
              </p>
              <p className="text-2xl font-bold text-blue-600">{displayStats.presence_rate || 0}%</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <span className="text-2xl">📊</span>
            </div>
          </div>
        </div>

        {/* Nouvelle carte pour les heures mensuelles */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl shadow-sm p-6 text-white">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-100">Heures ce mois</p>
              <p className="text-2xl font-bold">{monthlyStats?.monthly_data?.total_monthly_hours || 0}h</p>
              <p className="text-xs text-yellow-100 mt-1">
                {monthlyStats?.monthly_data?.month_name}
              </p>
            </div>
            <div className="p-3 bg-white/20 rounded-full">
              <span className="text-2xl">⏰</span>
            </div>
          </div>
        </div>
      </div>

      {/* Section des heures par employé - Admin seulement */}
      {!isEmployee && monthlyStats?.monthly_data?.employees_hours && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Heures par employé - {monthlyStats.monthly_data.month_name}
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(monthlyStats.monthly_data.employees_hours).map(([empId, data]) => (
                <div key={empId} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {data.employee_prenom} {data.employee_nom}
                      </p>
                      <p className="text-sm text-gray-600">
                        {data.entries.length} pointages
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-yellow-600">
                        {data.total_hours}h
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Liste des employés filtrée */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEmployee ? 'Mon État Actuel' : 'État des Employés'}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employé
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Poste
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernière activité
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayEmployees.map((employee) => (
                <tr key={employee.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                        {employee.prenom?.charAt(0)}{employee.nom?.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {employee.prenom} {employee.nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          {employee.departement}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.poste}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      employee.statut === 'Présent' ? 'bg-green-100 text-green-800' :
                      employee.statut === 'En pause' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      <span className="mr-1">
                        {employee.statut === 'Présent' ? '✅' : 
                         employee.statut === 'En pause' ? '⏸️' : '❌'}
                      </span>
                      {employee.statut}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {employee.derniere_activite ? 
                      new Date(employee.derniere_activite).toLocaleString('fr-FR') : 
                      'Aucune activité'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {displayEmployees.length === 0 && (
            <div className="text-center py-8">
              <div className="text-gray-400 text-3xl mb-3">👥</div>
              <p className="text-gray-500">
                {isEmployee ? 'Aucune donnée disponible' : 'Aucun employé trouvé'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Section Historique des pointages récents - Admin seulement */}
      {!isEmployee && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Historique des pointages récents</h3>
              <button
                onClick={() => setShowManualModal(true)}
                className="bg-gradient-to-r from-green-400 to-green-500 text-white px-4 py-2 rounded-lg hover:from-green-500 hover:to-green-600 transition-colors"
              >
                ➕ Ajouter pointage manuel
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {recentEntries.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employé</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Heure</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {entry.employee_prenom} {entry.employee_nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          {entry.employee_poste}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          entry.type_pointage === 'arrivee' ? 'bg-green-100 text-green-800' :
                          entry.type_pointage === 'depart' ? 'bg-red-100 text-red-800' :
                          entry.type_pointage === 'pause_debut' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {entry.type_pointage === 'arrivee' ? '🟢 Arrivée' :
                           entry.type_pointage === 'depart' ? '🔴 Départ' :
                           entry.type_pointage === 'pause_debut' ? '🟡 Pause' : '🔵 Reprise'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(entry.timestamp).toLocaleDateString('fr-FR')}
                        <br />
                        <span className="text-gray-500">
                          {new Date(entry.timestamp).toLocaleTimeString('fr-FR')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {entry.notes || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditEntry(entry)}
                            className="text-yellow-600 hover:text-yellow-900 bg-yellow-50 hover:bg-yellow-100 px-2 py-1 rounded transition-colors"
                          >
                            ✏️ Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
                          >
                            🗑️ Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-3xl mb-3">📋</div>
                <p className="text-gray-500">Aucun pointage récent</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Informations pour les employés */}
      {isEmployee && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">ℹ️ Accès Employé</h4>
          <p className="text-sm text-blue-700">
            Vous voyez uniquement vos propres données. Pour accéder à plus d'informations, 
            consultez l'onglet <strong>"Mes Données"</strong>.
          </p>
        </div>
      )}

      {/* Modal d'édition */}
      {showEditModal && editingEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Modifier le pointage</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employé
                </label>
                <p className="text-sm text-gray-900 p-2 bg-gray-50 rounded">
                  {editingEntry.employee_prenom} {editingEntry.employee_nom}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de pointage
                </label>
                <select
                  value={editingEntry.type_pointage}
                  onChange={(e) => setEditingEntry({...editingEntry, type_pointage: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="arrivee">Arrivée</option>
                  <option value="depart">Départ</option>
                  <option value="pause_debut">Début pause</option>
                  <option value="pause_fin">Fin pause</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date et heure
                </label>
                <input
                  type="datetime-local"
                  value={editingEntry.timestamp}
                  onChange={(e) => setEditingEntry({...editingEntry, timestamp: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={editingEntry.notes || ''}
                  onChange={(e) => setEditingEntry({...editingEntry, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout pointage manuel */}
      {showManualModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Ajouter un pointage manuel</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employé *
                </label>
                <select
                  value={manualEntry.employee_id}
                  onChange={(e) => setManualEntry({...manualEntry, employee_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  required
                >
                  <option value="">Sélectionner un employé</option>
                  {employees.map(employee => (
                    <option key={employee.id} value={employee.id}>
                      {employee.prenom} {employee.nom} - {employee.poste}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de pointage *
                </label>
                <select
                  value={manualEntry.type_pointage}
                  onChange={(e) => setManualEntry({...manualEntry, type_pointage: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="arrivee">Arrivée</option>
                  <option value="depart">Départ</option>
                  <option value="pause_debut">Début pause</option>
                  <option value="pause_fin">Fin pause</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date et heure *
                </label>
                <input
                  type="datetime-local"
                  value={manualEntry.timestamp}
                  onChange={(e) => setManualEntry({...manualEntry, timestamp: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={manualEntry.notes}
                  onChange={(e) => setManualEntry({...manualEntry, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="Raison de l'ajout manuel, oubli de pointage, etc."
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowManualModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveManual}
                disabled={!manualEntry.employee_id}
                className="flex-1 bg-gradient-to-r from-green-400 to-green-500 text-white px-4 py-2 rounded-lg hover:from-green-500 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Onglet Pointage
const PointageTab = ({ employees, onRefresh }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [pointageType, setPointageType] = useState('arrivee');
  const [location, setLocation] = useState(null);
  const [locationTimestamp, setLocationTimestamp] = useState(null);
  const [locationMethod, setLocationMethod] = useState('gps'); // 'gps' ou 'address'
  const [addressInput, setAddressInput] = useState('');
  const [photo, setPhoto] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);

  // Vérifie si la géolocalisation a expiré (plus de 10 minutes)
  const isLocationExpired = () => {
    if (!locationTimestamp) return true;
    const now = new Date().getTime();
    const elapsed = now - locationTimestamp;
    return elapsed > 10 * 60 * 1000; // 10 minutes en millisecondes
  };

  const getLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          setLocationTimestamp(new Date().getTime());
          setLocationLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Erreur lors de la récupération de la position GPS');
          setLocationLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 } // maximumAge à 0 pour forcer une nouvelle demande
      );
    } else {
      alert('La géolocalisation n\'est pas supportée par ce navigateur');
      setLocationLoading(false);
    }
  };

  const handleAddressGeocode = async () => {
    if (!addressInput.trim()) {
      alert('Veuillez saisir une adresse');
      return;
    }

    setAddressLoading(true);
    try {
      const result = await geocodeAddress(addressInput);
      setLocation({
        latitude: result.latitude,
        longitude: result.longitude,
        accuracy: 0, // Précision non disponible pour le géocodage
        address: result.display_name
      });
      setLocationTimestamp(new Date().getTime());
      alert(`Adresse géolocalisée avec succès !\n${result.display_name}`);
    } catch (error) {
      alert('Erreur lors de la géolocalisation de l\'adresse. Vérifiez l\'adresse saisie.');
    } finally {
      setAddressLoading(false);
    }
  };

  const handlePhotoCapture = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhoto(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier si la géolocalisation a expiré
    if (!location || isLocationExpired()) {
      alert('Votre position GPS a expiré. Veuillez obtenir une nouvelle position pour le pointage.');
      return;
    }
    
    if (!selectedEmployee) {
      alert('Veuillez sélectionner un employé');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/time-entries`, {
        employee_id: selectedEmployee,
        type_pointage: pointageType,
        localisation: location,
        photo_url: photo,
        notes: notes || null
      });

      alert('Pointage enregistré avec succès !');
      setSelectedEmployee('');
      setLocation(null);
      setLocationTimestamp(null);
      setAddressInput('');
      setPhoto(null);
      setNotes('');
      onRefresh();
    } catch (error) {
      console.error('Error creating time entry:', error);
      alert('Erreur lors de l\'enregistrement du pointage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Nouveau pointage</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employé
            </label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              required
            >
              <option value="">Sélectionner un employé</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.prenom} {employee.nom} - {employee.poste}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de pointage
            </label>
            <select
              value={pointageType}
              onChange={(e) => setPointageType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
            >
              <option value="arrivee">Arrivée</option>
              <option value="depart">Départ</option>
              <option value="pause_debut">Début de pause</option>
              <option value="pause_fin">Fin de pause</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Position GPS
            </label>
            
            {/* Sélecteur de méthode */}
            <div className="flex mb-3 bg-gray-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setLocationMethod('gps')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  locationMethod === 'gps'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                📍 GPS automatique
              </button>
              <button
                type="button"
                onClick={() => setLocationMethod('address')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  locationMethod === 'address'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                🏠 Saisir adresse
              </button>
            </div>

            {/* Méthode GPS */}
            {locationMethod === 'gps' && (
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={getLocation}
                  disabled={locationLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {locationLoading ? 'Obtention...' : 'Obtenir la position'}
                </button>
                {location && !isLocationExpired() && (
                  <div className="text-sm text-green-600 flex items-center">
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                    Position valide
                  </div>
                )}
                {location && isLocationExpired() && (
                  <div className="text-sm text-red-600 flex items-center">
                    <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                    Position expirée - Obtenir une nouvelle position
                  </div>
                )}
              </div>
            )}

            {/* Méthode adresse */}
            {locationMethod === 'address' && (
              <div className="space-y-2">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={addressInput}
                    onChange={(e) => setAddressInput(e.target.value)}
                    placeholder="Saisir une adresse complète..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                  <button
                    type="button"
                    onClick={handleAddressGeocode}
                    disabled={addressLoading || !addressInput.trim()}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addressLoading ? '🔄' : '🔍'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Exemple: 123 Rue de la Paix, 75001 Paris, France
                </p>
              </div>
            )}

            {/* Affichage des informations de position */}
            {location && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                <p>Latitude: {location.latitude.toFixed(6)}</p>
                <p>Longitude: {location.longitude.toFixed(6)}</p>
                {location.accuracy !== undefined && location.accuracy > 0 && (
                  <p>Précision: {Math.round(location.accuracy)}m</p>
                )}
                {location.address && (
                  <p className="text-gray-600 mt-1">Adresse: {location.address}</p>
                )}
                {locationTimestamp && (
                  <p className="text-gray-500 mt-1">
                    Obtenue: {new Date(locationTimestamp).toLocaleTimeString('fr-FR')}
                    {isLocationExpired() && <span className="text-red-600 font-medium"> (Expirée)</span>}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo (optionnel)
            </label>
            <input
              type="file"
              accept="image/*"
              capture="user"
              onChange={handlePhotoCapture}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
            />
            {photo && (
              <div className="mt-3">
                <img src={photo} alt="Photo de pointage" className="w-32 h-32 object-cover rounded-lg" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              placeholder="Ajouter des notes..."
            />
          </div>

          <button
            type="submit"
            disabled={loading || !selectedEmployee || !location || isLocationExpired()}
            className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold py-3 px-6 rounded-xl hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Enregistrement...' : 'Enregistrer le pointage'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Onglet Employés
const EmployeesTab = ({ employees, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedEmployeeQR, setSelectedEmployeeQR] = useState(null);
  const [qrCodeDataURL, setQrCodeDataURL] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    poste: '',
    departement: '',
    telephone: '',
    photo_required: false,  // NOUVEAU: Photo obligatoire pour cet employé
    adresse_travail: '',
    latitude_travail: null,
    longitude_travail: null,
    sites_autorises: []
  });
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [geocodingAddress, setGeocodingAddress] = useState(false);

  const generateQRCode = async (employee) => {
    try {
      // URL pour le pointage direct de l'employé
      const pointageURL = `${window.location.origin}/pointage-direct/${employee.id}`;
      const qrDataURL = await QRCode.toDataURL(pointageURL, {
        width: 300,
        margin: 2,
        color: {
          dark: '#B8860B', // Couleur dorée pour le QR code
          light: '#FFFFFF'
        }
      });
      setQrCodeDataURL(qrDataURL);
      setSelectedEmployeeQR(employee);
      setShowQRModal(true);
    } catch (error) {
      console.error('Erreur génération QR code:', error);
      alert('Erreur lors de la génération du QR code');
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeDataURL || !selectedEmployeeQR) return;
    
    const link = document.createElement('a');
    link.download = `QR_${selectedEmployeeQR.prenom}_${selectedEmployeeQR.nom}.png`;
    link.href = qrCodeDataURL;
    link.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, formData);
        alert('Employé modifié avec succès !');
      } else {
        await axios.post(`${API}/employees`, formData);
        alert('Employé ajouté avec succès !');
      }
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('Error saving employee:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      nom: employee.nom,
      prenom: employee.prenom,
      email: employee.email || '',
      poste: employee.poste,
      departement: employee.departement,
      telephone: employee.telephone || '',
      photo_required: employee.photo_required || false,  // NOUVEAU: Récupérer photo_required
      adresse_travail: employee.adresse_travail || '',
      latitude_travail: employee.latitude_travail || null,
      longitude_travail: employee.longitude_travail || null,
      sites_autorises: employee.sites_autorises || []
    });
    setShowForm(true);
  };

  const getCurrentLocation = () => {
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData({
          ...formData,
          latitude_travail: position.coords.latitude,
          longitude_travail: position.coords.longitude,
          adresse_travail: `Coordonnées: ${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`
        });
        setGettingLocation(false);
        setUseCurrentLocation(true);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Erreur lors de la récupération de la position GPS');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  const handleGeocodeAddress = async () => {
    if (!formData.adresse_travail.trim()) {
      alert('Veuillez saisir une adresse');
      return;
    }

    setGeocodingAddress(true);
    try {
      const result = await geocodeAddress(formData.adresse_travail);
      setFormData({
        ...formData,
        latitude_travail: result.latitude,
        longitude_travail: result.longitude,
        adresse_travail: result.display_name
      });
      alert(`Adresse géolocalisée avec succès !\n${result.display_name}`);
    } catch (error) {
      alert('Erreur lors de la géolocalisation de l\'adresse. Vérifiez l\'adresse saisie.');
    } finally {
      setGeocodingAddress(false);
    }
  };

  const handleDelete = async (employeeId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet employé ?')) {
      try {
        await axios.delete(`${API}/employees/${employeeId}`);
        alert('Employé supprimé avec succès !');
        onRefresh();
      } catch (error) {
        console.error('Error deleting employee:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      poste: '',
      departement: '',
      telephone: '',
      photo_required: false,  // NOUVEAU: Reset photo_required
      adresse_travail: '',
      latitude_travail: null,
      longitude_travail: null,
      sites_autorises: []
    });
    setEditingEmployee(null);
    setShowForm(false);
    setUseCurrentLocation(false);
    setGeocodingAddress(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Présent': return 'bg-green-100 text-green-800';
      case 'En pause': return 'bg-orange-100 text-orange-800';
      case 'Absent': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Présent': return '✅';
      case 'En pause': return '⏸️';
      case 'Absent': return '❌';
      default: return '⭕';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestion des employés</h2>
        <button
          onClick={() => setShowForm(true)}
          className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all"
        >
          + Ajouter un employé
        </button>
      </div>

      {/* QR Code Modal */}
      {showQRModal && selectedEmployeeQR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                QR Code - {selectedEmployeeQR.prenom} {selectedEmployeeQR.nom}
              </h3>
              
              <div className="mb-6 flex justify-center">
                <div className="p-4 bg-white border-4 border-yellow-400 rounded-lg shadow-lg">
                  <img src={qrCodeDataURL} alt="QR Code" className="w-64 h-64" />
                </div>
              </div>
              
              <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Instructions:</strong>
                </p>
                <p className="text-xs text-gray-600">
                  L'employé peut scanner ce QR code avec son smartphone pour accéder directement à sa page de pointage personnalisée.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  URL: /pointage-direct/{selectedEmployeeQR.id}
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowQRModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Fermer
                </button>
                <button
                  onClick={downloadQRCode}
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600"
                >
                  📥 Télécharger
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-90vh overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingEmployee ? 'Modifier l\'employé' : 'Ajouter un employé'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (optionnel)</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Poste</label>
                <input
                  type="text"
                  value={formData.poste}
                  onChange={(e) => setFormData({...formData, poste: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
                <input
                  type="text"
                  value={formData.departement}
                  onChange={(e) => setFormData({...formData, departement: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
              </div>
              
              {/* NOUVEAU: Configuration Photo */}
              <div className="border-t pt-4">
                <h4 className="text-md font-semibold text-gray-800 mb-3">📸 Configuration Photo</h4>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="photo_required"
                    checked={formData.photo_required}
                    onChange={(e) => setFormData({...formData, photo_required: e.target.checked})}
                    className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                  />
                  <label htmlFor="photo_required" className="text-sm font-medium text-gray-700">
                    📷 Rendre la photo obligatoire pour cet employé
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2 ml-7">
                  {formData.photo_required 
                    ? "⚠️ Cet employé devra obligatoirement prendre une photo lors de chaque pointage"
                    : "ℹ️ Les photos seront optionnelles pour cet employé (par défaut)"
                  }
                </p>
              </div>
              
              {/* Section Lieu de travail */}
              <div className="border-t pt-4">
                <h4 className="text-md font-semibold text-gray-800 mb-3">📍 Lieu de travail</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse du lieu de travail
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={formData.adresse_travail}
                        onChange={(e) => setFormData({...formData, adresse_travail: e.target.value})}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        placeholder="Ex: 123 Rue de la Paix, 75001 Paris, France"
                      />
                      <button
                        type="button"
                        onClick={handleGeocodeAddress}
                        disabled={geocodingAddress || !formData.adresse_travail.trim()}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {geocodingAddress ? '🔄' : '🔍'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Saisir l'adresse complète puis cliquer sur 🔍 pour géolocaliser, ou utiliser le GPS ci-dessous
                    </p>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={getCurrentLocation}
                      disabled={gettingLocation}
                      className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                    >
                      {gettingLocation ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          Position...
                        </div>
                      ) : (
                        '📍 Utiliser ma position GPS actuelle'
                      )}
                    </button>
                  </div>
                  
                  {(formData.latitude_travail && formData.longitude_travail) && (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-sm text-green-700">
                        <strong>✓ Position enregistrée</strong>
                        <br />
                        Lat: {formData.latitude_travail.toFixed(6)}
                        <br />
                        Lng: {formData.longitude_travail.toFixed(6)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600"
                >
                  {editingEmployee ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Employees List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employé
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Poste
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Département
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Photo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employees.map(employee => (
                <tr key={employee.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                        <span className="text-gray-600 font-semibold">
                          {employee.prenom.charAt(0)}{employee.nom.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {employee.prenom} {employee.nom}
                        </div>
                        <div className="text-sm text-gray-500">{employee.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.poste}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.departement}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      employee.photo_required 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      <span className="mr-1">{employee.photo_required ? '📷' : '📸'}</span>
                      {employee.photo_required ? 'Obligatoire' : 'Optionnelle'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(employee.statut)}`}>
                      <span className="mr-1">{getStatusIcon(employee.statut)}</span>
                      {employee.statut}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => generateQRCode(employee)}
                      className="text-blue-600 hover:text-blue-900 font-medium"
                      title="Générer QR Code"
                    >
                      📱 QR Code
                    </button>
                    <button
                      onClick={() => handleEdit(employee)}
                      className="text-yellow-600 hover:text-yellow-900"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(employee.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {employees.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun employé trouvé</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Onglet Calendrier mensuel
const CalendarTab = ({ employees }) => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCalendarData();
  }, [selectedMonth]);

  const loadCalendarData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/calendar/${selectedMonth}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    for (let i = -12; i <= 3; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // Fix: month + 1
      const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    
    return options;
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Lundi = 0
  };

  const renderCalendar = () => {
    if (!calendarData) return null;

    const [year, month] = selectedMonth.split('-').map(Number);
    const daysInMonth = getDaysInMonth(year, month - 1);
    const firstDay = getFirstDayOfMonth(year, month - 1);
    
    const calendar = [];
    
    // Header des jours de la semaine
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
    calendar.push(
      <div key="header" className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map(day => (
          <div key={day} className="p-2 text-center font-semibold text-gray-600 bg-gray-100 rounded">
            {day}
          </div>
        ))}
      </div>
    );

    // Cases vides pour aligner le premier jour
    const totalCells = Math.ceil((daysInMonth + firstDay) / 7) * 7;
    const cells = [];

    for (let i = 0; i < totalCells; i++) {
      const dayNumber = i - firstDay + 1;
      
      if (i < firstDay || dayNumber > daysInMonth) {
        // Cellule vide
        cells.push(
          <div key={`empty-${i}`} className="h-24 bg-gray-50 rounded border"></div>
        );
      } else {
        // Cellule avec jour
        const dayEntries = calendarData.calendar_data[dayNumber] || [];
        
        cells.push(
          <div key={dayNumber} className="h-24 bg-white rounded border border-gray-200 p-1 overflow-y-auto">
            <div className="font-semibold text-sm text-gray-700 mb-1">{dayNumber}</div>
            <div className="space-y-1">
              {dayEntries.map(entry => (
                <div
                  key={entry.id}
                  className={`text-xs p-1 rounded ${
                    entry.type_pointage === 'arrivee' ? 'bg-green-100 text-green-700' :
                    entry.type_pointage === 'depart' ? 'bg-red-100 text-red-700' :
                    entry.type_pointage === 'pause_debut' ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-100 text-blue-700'
                  }`}
                >
                  <div className="font-medium">{entry.employee_prenom} {entry.employee_nom}</div>
                  <div className="text-xs">
                    {entry.type_pointage === 'arrivee' ? '🟢 Arrivée' :
                     entry.type_pointage === 'depart' ? '🔴 Départ' :
                     entry.type_pointage === 'pause_debut' ? '🟡 Pause' : '🔵 Reprise'}
                  </div>
                  <div className="text-xs">
                    {new Date(entry.timestamp).toLocaleTimeString('fr-FR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }
    }

    // Grouper les cellules en semaines
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(
        <div key={`week-${i/7}`} className="grid grid-cols-7 gap-1 mb-1">
          {cells.slice(i, i + 7)}
        </div>
      );
    }

    return weeks;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Calendrier des pointages</h2>
        
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Mois :</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
          >
            {generateMonthOptions().map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mr-3"></div>
          <span>Chargement du calendrier...</span>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">
            {calendarData?.month_name} - {calendarData?.total_entries || 0} pointages
          </h3>
          
          <div className="calendar-container">
            {renderCalendar()}
          </div>

          {/* Légende */}
          <div className="mt-6 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-100 rounded"></div>
              <span>Arrivée</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-100 rounded"></div>
              <span>Départ</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-orange-100 rounded"></div>
              <span>Début pause</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-100 rounded"></div>
              <span>Fin pause</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Onglet Notifications Email
const NotificationsTab = ({ employees }) => {
  const [emailConfigs, setEmailConfigs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // NOUVEAU: États pour configuration SMTP
  const [showSMTPForm, setShowSMTPForm] = useState(false);
  const [smtpConfig, setSMTPConfig] = useState(null);
  const [smtpFormData, setSMTPFormData] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'ROUCH POINTAGE',
    smtp_use_ssl: false
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [detectingConfig, setDetectingConfig] = useState(false);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    email_recipients: [''],
    notify_on_arrival: true,
    notify_on_departure: true,
    notify_on_break: false,
    active: true
  });

  useEffect(() => {
    loadEmailConfigs();
    loadSMTPConfig(); // NOUVEAU: Charger la config SMTP actuelle
  }, []);

  const loadEmailConfigs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/email-notifications`);
      setEmailConfigs(response.data);
    } catch (error) {
      console.error('Error loading email configs:', error);
    } finally {
      setLoading(false);
    }
  };

  // NOUVEAU: Fonctions de gestion SMTP
  const loadSMTPConfig = async () => {
    try {
      const response = await axios.get(`${API}/smtp-config`);
      setSMTPConfig(response.data);
      if (response.data) {
        setSMTPFormData({
          smtp_host: response.data.smtp_host,
          smtp_port: response.data.smtp_port,
          smtp_username: response.data.smtp_username,
          smtp_password: '', // Ne pas afficher le mot de passe
          smtp_from_email: response.data.smtp_from_email,
          smtp_from_name: response.data.smtp_from_name,
          smtp_use_ssl: response.data.smtp_use_ssl
        });
      }
    } catch (error) {
      console.error('Error loading SMTP config:', error);
    }
  };

  const handleSMTPSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/smtp-config`, smtpFormData);
      alert('Configuration SMTP sauvegardée avec succès !');
      setShowSMTPForm(false);
      loadSMTPConfig();
    } catch (error) {
      console.error('Error saving SMTP config:', error);
      alert('Erreur lors de la sauvegarde de la configuration SMTP');
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await axios.post(`${API}/smtp-config/test`, smtpFormData);
      if (response.data.success) {
        alert('✅ Test réussi ! ' + response.data.message);
      } else {
        alert('❌ Test échoué : ' + response.data.message);
      }
    } catch (error) {
      console.error('Error testing SMTP:', error);
      alert('❌ Erreur lors du test de connexion SMTP');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDetectConfig = async () => {
    if (!smtpFormData.smtp_from_email) {
      alert('Veuillez d\'abord saisir l\'adresse email d\'expédition');
      return;
    }
    
    setDetectingConfig(true);
    try {
      const response = await axios.get(`${API}/smtp-config/detect?email_domain=${smtpFormData.smtp_from_email}`);
      if (response.data.detected) {
        const config = response.data.config;
        setSMTPFormData({
          ...smtpFormData,
          smtp_host: config.smtp_host,
          smtp_port: config.smtp_port,
          smtp_use_ssl: config.smtp_use_ssl
        });
        alert(`✅ Configuration détectée automatiquement !\n${config.info}`);
      } else {
        const suggestions = response.data.suggestions;
        setSMTPFormData({
          ...smtpFormData,
          smtp_host: suggestions.smtp_host,
          smtp_port: suggestions.smtp_port,
          smtp_use_ssl: suggestions.smtp_use_ssl
        });
        alert(`ℹ️ ${response.data.message}\nConfiguration générique appliquée.`);
      }
    } catch (error) {
      console.error('Error detecting SMTP config:', error);
      alert('Erreur lors de la détection automatique');
    } finally {
      setDetectingConfig(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out empty email addresses
    const cleanedEmails = formData.email_recipients.filter(email => email.trim() !== '');
    if (cleanedEmails.length === 0) {
      alert('Veuillez ajouter au moins une adresse email');
      return;
    }

    try {
      const payload = {
        ...formData,
        email_recipients: cleanedEmails,
        employee_id: formData.employee_id || null
      };

      if (editingConfig) {
        await axios.put(`${API}/email-notifications/${editingConfig.id}`, payload);
        alert('Configuration modifiée avec succès !');
      } else {
        await axios.post(`${API}/email-notifications`, payload);
        alert('Configuration ajoutée avec succès !');
      }
      
      resetForm();
      loadEmailConfigs();
    } catch (error) {
      console.error('Error saving email config:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      employee_id: config.employee_id || '',
      email_recipients: config.email_recipients.length > 0 ? config.email_recipients : [''],
      notify_on_arrival: config.notify_on_arrival,
      notify_on_departure: config.notify_on_departure,
      notify_on_break: config.notify_on_break,
      active: config.active
    });
    setShowForm(true);
  };

  const handleDelete = async (configId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette configuration ?')) {
      try {
        await axios.delete(`${API}/email-notifications/${configId}`);
        alert('Configuration supprimée avec succès !');
        loadEmailConfigs();
      } catch (error) {
        console.error('Error deleting config:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      email_recipients: [''],
      notify_on_arrival: true,
      notify_on_departure: true,
      notify_on_break: false,
      active: true
    });
    setEditingConfig(null);
    setShowForm(false);
  };

  const addEmailField = () => {
    setFormData({
      ...formData,
      email_recipients: [...formData.email_recipients, '']
    });
  };

  const removeEmailField = (index) => {
    const newEmails = formData.email_recipients.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      email_recipients: newEmails.length > 0 ? newEmails : ['']
    });
  };

  const updateEmailField = (index, value) => {
    const newEmails = [...formData.email_recipients];
    newEmails[index] = value;
    setFormData({
      ...formData,
      email_recipients: newEmails
    });
  };

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return 'Tous les employés';
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.prenom} ${employee.nom}` : 'Employé non trouvé';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Notifications Email</h2>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowSMTPForm(true)}
            className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all"
          >
            ⚙️ Config SMTP
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-blue-500 hover:to-blue-600 transition-all"
          >
            + Nouvelle configuration
          </button>
        </div>
      </div>

      {/* NOUVEAU: Section Configuration SMTP */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-yellow-800">🔧 Configuration Serveur Email (SMTP)</h3>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            smtpConfig ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {smtpConfig ? '✅ Configuré' : '❌ Non configuré'}
          </span>
        </div>
        <div className="text-sm text-yellow-700">
          {smtpConfig ? (
            <div className="space-y-1">
              <p>• <strong>Serveur:</strong> {smtpConfig.smtp_host}:{smtpConfig.smtp_port} ({smtpConfig.smtp_use_ssl ? 'SSL' : 'STARTTLS'})</p>
              <p>• <strong>Expéditeur:</strong> {smtpConfig.smtp_from_name} &lt;{smtpConfig.smtp_from_email}&gt;</p>
              <p>• <strong>Compte:</strong> {smtpConfig.smtp_username}</p>
            </div>
          ) : (
            <p>⚠️ Aucune configuration SMTP définie. Les emails ne peuvent pas être envoyés.</p>
          )}
        </div>
      </div>

      {/* Information sur le système */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">📧 Fonctionnement des notifications</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Les emails sont envoyés automatiquement lors des pointages</li>
          <li>• Vous pouvez configurer des notifications par employé ou globales</li>
          <li>• Personnalisez les types de pointages qui déclenchent les notifications</li>
          <li>• Ajoutez plusieurs destinataires pour chaque configuration</li>
        </ul>
      </div>

      {/* Liste des configurations */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Configurations actives ({emailConfigs.length})
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
            <span>Chargement...</span>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {emailConfigs.length > 0 ? (
              emailConfigs.map(config => (
                <div key={config.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-semibold text-gray-900">
                          {getEmployeeName(config.employee_id)}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          config.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {config.active ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>Destinataires:</strong> {config.email_recipients.join(', ')}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm">
                        <span className={`flex items-center ${config.notify_on_arrival ? 'text-green-600' : 'text-gray-400'}`}>
                          {config.notify_on_arrival ? '✅' : '❌'} Arrivées
                        </span>
                        <span className={`flex items-center ${config.notify_on_departure ? 'text-green-600' : 'text-gray-400'}`}>
                          {config.notify_on_departure ? '✅' : '❌'} Départs
                        </span>
                        <span className={`flex items-center ${config.notify_on_break ? 'text-green-600' : 'text-gray-400'}`}>
                          {config.notify_on_break ? '✅' : '❌'} Pauses
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(config)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(config.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Aucune configuration email configurée</p>
                <p className="text-sm text-gray-400 mt-1">Cliquez sur "Nouvelle configuration" pour commencer</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de formulaire */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-90vh overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingConfig ? 'Modifier la configuration' : 'Nouvelle configuration email'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employé concerné
                </label>
                <select
                  value={formData.employee_id}
                  onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">Tous les employés (configuration globale)</option>
                  {employees.map(employee => (
                    <option key={employee.id} value={employee.id}>
                      {employee.prenom} {employee.nom} - {employee.poste}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Laissez vide pour appliquer à tous les employés
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Destinataires des notifications *
                </label>
                {formData.email_recipients.map((email, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => updateEmailField(index, e.target.value)}
                      placeholder="email@exemple.com"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    {formData.email_recipients.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmailField(index)}
                        className="text-red-600 hover:text-red-800 px-2"
                      >
                        ❌
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addEmailField}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  + Ajouter une adresse email
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Types de pointages à notifier
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.notify_on_arrival}
                      onChange={(e) => setFormData({...formData, notify_on_arrival: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm">🟢 Arrivées</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.notify_on_departure}
                      onChange={(e) => setFormData({...formData, notify_on_departure: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm">🔴 Départs</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.notify_on_break}
                      onChange={(e) => setFormData({...formData, notify_on_break: e.target.checked})}
                      className="mr-2"
                    />
                    <span className="text-sm">🟡 Pauses (début et fin)</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-400 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-blue-500 hover:to-blue-600"
                >
                  {editingConfig ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NOUVEAU: Modal Configuration SMTP */}
      {showSMTPForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-90vh overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">⚙️ Configuration Serveur Email (SMTP)</h3>
              <button
                onClick={() => setShowSMTPForm(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSMTPSubmit} className="space-y-4">
              {/* Auto-détection */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">🔍 Auto-détection</h4>
                <p className="text-sm text-blue-700 mb-3">
                  Saisissez d'abord votre adresse email d'expédition, puis cliquez pour détecter automatiquement la configuration.
                </p>
                <button
                  type="button"
                  onClick={handleDetectConfig}
                  disabled={detectingConfig || !smtpFormData.smtp_from_email}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {detectingConfig ? '🔍 Détection...' : '🔍 Détecter automatiquement'}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Email d'expédition */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email d'expédition *
                  </label>
                  <input
                    type="email"
                    value={smtpFormData.smtp_from_email}
                    onChange={(e) => setSMTPFormData({...smtpFormData, smtp_from_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="pointage@votre-domaine.com"
                    required
                  />
                </div>

                {/* Nom d'expédition */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom d'expédition
                  </label>
                  <input
                    type="text"
                    value={smtpFormData.smtp_from_name}
                    onChange={(e) => setSMTPFormData({...smtpFormData, smtp_from_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="ROUCH POINTAGE"
                  />
                </div>

                {/* Serveur SMTP */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Serveur SMTP *
                  </label>
                  <input
                    type="text"
                    value={smtpFormData.smtp_host}
                    onChange={(e) => setSMTPFormData({...smtpFormData, smtp_host: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="smtp.votre-fournisseur.com"
                    required
                  />
                </div>

                {/* Port */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Port *
                  </label>
                  <select
                    value={smtpFormData.smtp_port}
                    onChange={(e) => setSMTPFormData({...smtpFormData, smtp_port: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  >
                    <option value={465}>465 (SSL)</option>
                    <option value={587}>587 (STARTTLS)</option>
                    <option value={25}>25 (Non sécurisé)</option>
                  </select>
                </div>

                {/* SSL */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={smtpFormData.smtp_use_ssl}
                      onChange={(e) => setSMTPFormData({...smtpFormData, smtp_use_ssl: e.target.checked})}
                      className="mr-2 h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Utiliser SSL (port 465)</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Décochez pour STARTTLS (port 587)
                  </p>
                </div>

                {/* Nom d'utilisateur */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom d'utilisateur *
                  </label>
                  <input
                    type="text"
                    value={smtpFormData.smtp_username}
                    onChange={(e) => setSMTPFormData({...smtpFormData, smtp_username: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="votre-nom-utilisateur"
                    required
                  />
                </div>

                {/* Mot de passe */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe *
                  </label>
                  <input
                    type="password"
                    value={smtpFormData.smtp_password}
                    onChange={(e) => setSMTPFormData({...smtpFormData, smtp_password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder={smtpConfig ? "Laissez vide pour garder l'actuel" : "Votre mot de passe"}
                    required={!smtpConfig}
                  />
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSMTPForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testingConnection}
                  className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testingConnection ? '🧪 Test...' : '🧪 Tester connexion'}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-yellow-500 hover:to-orange-600"
                >
                  💾 Sauvegarder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Onglet Rapports
const ReportsTab = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState({
    debut: new Date().toISOString().split('T')[0],
    fin: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadTimeEntries();
  }, [dateFilter]);

  const loadTimeEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFilter.debut) params.append('date_debut', dateFilter.debut + 'T00:00:00Z');
      if (dateFilter.fin) params.append('date_fin', dateFilter.fin + 'T23:59:59Z');
      
      const response = await axios.get(`${API}/time-entries?${params}`);
      setTimeEntries(response.data);
    } catch (error) {
      console.error('Error loading time entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToXLSX = () => {
    if (timeEntries.length === 0) {
      alert('Aucune donnée à exporter pour cette période');
      return;
    }

    // Préparer les données pour l'export
    const exportData = timeEntries.map(entry => ({
      'Employé': `${entry.employee_prenom} ${entry.employee_nom}`,
      'Type de pointage': getTypeLabel(entry.type_pointage),
      'Date': new Date(entry.timestamp).toLocaleDateString('fr-FR'),
      'Heure': new Date(entry.timestamp).toLocaleTimeString('fr-FR'),
      'Latitude': entry.localisation?.latitude?.toFixed(6) || 'N/A',
      'Longitude': entry.localisation?.longitude?.toFixed(6) || 'N/A',
      'Notes': entry.notes || '',
      'Site': entry.site_nom || '',
      'Validé': entry.valide ? 'Oui' : 'Non'
    }));

    // Créer le classeur Excel
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    
    // Ajuster la largeur des colonnes
    const columnWidths = [
      { wch: 20 }, // Employé
      { wch: 15 }, // Type de pointage
      { wch: 12 }, // Date
      { wch: 12 }, // Heure
      { wch: 12 }, // Latitude
      { wch: 12 }, // Longitude
      { wch: 30 }, // Notes
      { wch: 15 }, // Site
      { wch: 8 }   // Validé
    ];
    worksheet['!cols'] = columnWidths;

    // Ajouter la feuille au classeur
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pointages');

    // Générer le nom de fichier avec la période
    const startDate = new Date(dateFilter.debut).toLocaleDateString('fr-FR');
    const endDate = new Date(dateFilter.fin).toLocaleDateString('fr-FR');
    const filename = `ROUCH_Pointages_${startDate.replace(/\//g, '-')}_au_${endDate.replace(/\//g, '-')}.xlsx`;

    // Sauvegarder le fichier
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, filename);
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'arrivee': return 'Arrivée';
      case 'depart': return 'Départ';
      case 'pause_debut': return 'Début pause';
      case 'pause_fin': return 'Fin pause';
      default: return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'arrivee': return 'bg-green-100 text-green-800';
      case 'depart': return 'bg-red-100 text-red-800';
      case 'pause_debut': return 'bg-orange-100 text-orange-800';
      case 'pause_fin': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Rapports des pointages</h2>
        <button
          onClick={exportToXLSX}
          disabled={timeEntries.length === 0}
          className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <span>📊</span>
          <span>Exporter XLSX</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">Filtres de période</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date de début</label>
            <input
              type="date"
              value={dateFilter.debut}
              onChange={(e) => setDateFilter({...dateFilter, debut: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date de fin</label>
            <input
              type="date"
              value={dateFilter.fin}
              onChange={(e) => setDateFilter({...dateFilter, fin: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
          <div>
            <div className="text-sm text-gray-600">
              <strong>{timeEntries.length}</strong> pointages trouvés
              {timeEntries.length > 0 && (
                <span className="text-green-600 ml-2">✓ Prêt pour export</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Raccourcis de période */}
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              setDateFilter({ debut: today, fin: today });
            }}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          >
            Aujourd'hui
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
              setDateFilter({ 
                debut: weekAgo.toISOString().split('T')[0], 
                fin: today.toISOString().split('T')[0] 
              });
            }}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          >
            7 derniers jours
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
              setDateFilter({ 
                debut: monthAgo.toISOString().split('T')[0], 
                fin: today.toISOString().split('T')[0] 
              });
            }}
            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          >
            30 derniers jours
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            Pointages ({timeEntries.length})
          </h3>
          {timeEntries.length > 0 && (
            <div className="text-sm text-gray-600">
              Du {new Date(dateFilter.debut).toLocaleDateString('fr-FR')} au {new Date(dateFilter.fin).toLocaleDateString('fr-FR')}
            </div>
          )}
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employé
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date/Heure
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {timeEntries.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {entry.employee_prenom} {entry.employee_nom}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(entry.type_pointage)}`}>
                        {getTypeLabel(entry.type_pointage)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{new Date(entry.timestamp).toLocaleDateString('fr-FR')}</div>
                      <div className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleTimeString('fr-FR')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {entry.localisation ? 
                        <div className="text-xs">
                          <div>Lat: {entry.localisation.latitude?.toFixed(4)}</div>
                          <div>Lng: {entry.localisation.longitude?.toFixed(4)}</div>
                        </div>
                        : 'N/A'
                      }
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {entry.notes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {timeEntries.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-4">📋</div>
                <p className="text-gray-500 text-lg mb-2">Aucun pointage trouvé</p>
                <p className="text-gray-400 text-sm">Ajustez les filtres de date pour voir les pointages</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// NOUVEAU: Onglet Configuration - Gestion logo, nom entreprise et utilisateurs
const ConfigurationTab = () => {
  const [companyConfig, setCompanyConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    logo_base64: null
  });

  // NOUVEAU: États pour gestion des utilisateurs (admin ET employé)
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userFormData, setUserFormData] = useState({
    username: '',
    password: '',
    role: 'employee',
    employee_id: ''
  });

  useEffect(() => {
    loadCompanyConfig();
    loadUsers();
    loadEmployees();
  }, []);

  const loadCompanyConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/company-config`);
      setCompanyConfig(response.data);
      setFormData({
        company_name: response.data.company_name || 'ROUCH POINTAGE',
        logo_base64: response.data.logo_base64 || null
      });
    } catch (error) {
      console.error('Error loading company config:', error);
    } finally {
      setLoading(false);
    }
  };

  // NOUVEAU: Fonctions de gestion des utilisateurs
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      // Préparer les données selon le rôle
      const userData = {
        username: userFormData.username,
        password: userFormData.password,
        role: userFormData.role
      };
      
      // Ajouter employee_id seulement si c'est un employé
      if (userFormData.role === 'employee' && userFormData.employee_id) {
        userData.employee_id = userFormData.employee_id;
      }
      
      await axios.post(`${API}/users`, userData);
      alert('Compte utilisateur créé avec succès !');
      setShowUserForm(false);
      setUserFormData({
        username: '',
        password: '',
        role: 'employee',
        employee_id: ''
      });
      loadUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Erreur lors de la création du compte : ' + (error.response?.data?.detail || 'Erreur inconnue'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce compte utilisateur ?')) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        alert('Compte utilisateur supprimé avec succès !');
        loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.prenom} ${employee.nom}` : 'Employé non trouvé';
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB max
        alert('Le fichier est trop volumineux. Taille maximum : 2MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image valide');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData({
          ...formData,
          logo_base64: e.target.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFormData({
      ...formData,
      logo_base64: null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (companyConfig && companyConfig.id !== 'default') {
        // Mettre à jour configuration existante
        await axios.put(`${API}/company-config/${companyConfig.id}`, formData);
      } else {
        // Créer nouvelle configuration
        await axios.post(`${API}/company-config`, formData);
      }
      
      alert('Configuration sauvegardée avec succès !');
      loadCompanyConfig();
      
      // Recharger la page pour appliquer les changements partout
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Error saving company config:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mr-3"></div>
        <span>Chargement de la configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Configuration Entreprise</h2>
      </div>

      {/* Aperçu actuel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Aperçu actuel</h3>
        <div className="flex items-center space-x-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
          {/* Logo */}
          <div className="flex-shrink-0">
            {formData.logo_base64 ? (
              <img 
                src={formData.logo_base64} 
                alt="Logo entreprise" 
                className="w-16 h-16 rounded-full object-cover border-2 border-yellow-400"
              />
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center border-2 border-yellow-400">
                <span className="text-white font-bold text-xl">🏢</span>
              </div>
            )}
          </div>
          
          {/* Nom */}
          <div>
            <h4 className="text-xl font-bold text-purple-800">{formData.company_name}</h4>
            <p className="text-sm text-purple-600">SYSTÈME RH AVANCÉ</p>
            <p className="text-xs text-purple-500">Système de gestion des pointages</p>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-3">
          ℹ️ Cette configuration s'appliquera à toute l'application, y compris la page de connexion
        </p>
      </div>

      {/* Formulaire de configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Personnaliser l'identité visuelle</h3>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Nom de l'entreprise */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom de l'entreprise *
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({...formData, company_name: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 text-lg"
              placeholder="ROUCH POINTAGE"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Ce nom apparaîtra dans toute l'application et sur la page de connexion
            </p>
          </div>

          {/* Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo de l'entreprise
            </label>
            
            {formData.logo_base64 ? (
              <div className="space-y-4">
                {/* Aperçu logo */}
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <img 
                    src={formData.logo_base64} 
                    alt="Logo aperçu" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Logo actuel</p>
                    <p className="text-xs text-gray-500">Taille recommandée : 200x200px</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="ml-auto text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    🗑️ Supprimer
                  </button>
                </div>
                
                {/* Remplacer logo */}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-replace"
                  />
                  <label
                    htmlFor="logo-replace"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    🔄 Remplacer le logo
                  </label>
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <span className="text-3xl mb-2">📁</span>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Cliquez pour télécharger</span> un logo
                    </p>
                    <p className="text-xs text-gray-500">PNG, JPG ou GIF (MAX. 2MB)</p>
                  </div>
                </label>
              </div>
            )}
            
            <div className="mt-2 text-xs text-gray-500">
              <p>• Le logo apparaîtra dans l'en-tête de l'application et sur la page de connexion</p>
              <p>• Format recommandé : carré (200x200px) pour un meilleur rendu</p>
              <p>• Taille maximum : 2MB</p>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={loadCompanyConfig}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              🔄 Annuler les modifications
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-3 rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sauvegarde...
                </div>
              ) : (
                '💾 Sauvegarder les modifications'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* NOUVEAU: Section Gestion des Utilisateurs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">👥 Gestion des Comptes Utilisateurs</h3>
          <button
            onClick={() => setShowUserForm(true)}
            className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-blue-500 hover:to-blue-600 transition-all"
          >
            + Créer un compte
          </button>
        </div>

        {/* Liste des utilisateurs */}
        {loadingUsers ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
            <span>Chargement des comptes...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employé lié
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date création
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Utilisateur Jordan hardcodé */}
                <tr className="bg-yellow-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                        <span className="text-white font-bold text-sm">👑</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">Jordan</div>
                        <div className="text-xs text-gray-500">Compte principal</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      👑 Super Admin
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    -
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    Système
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-400">
                    Non supprimable
                  </td>
                </tr>
                
                {/* Utilisateurs de la base de données */}
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                          user.role === 'admin' ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                          <span className={`font-bold text-sm ${
                            user.role === 'admin' ? 'text-red-600' : 'text-blue-600'
                          }`}>
                            {user.role === 'admin' ? '⚡' : '👤'}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.username}</div>
                          <div className="text-xs text-gray-500">ID: {user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'admin' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role === 'admin' ? '⚡ Administrateur' : '👤 Employé'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.employee_id ? getEmployeeName(user.employee_id) : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.date_creation).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900 font-medium"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {users.length === 0 && (
              <div className="text-center py-8">
                <div className="text-gray-400 text-3xl mb-3">👥</div>
                <p className="text-gray-500">Aucun compte utilisateur créé</p>
                <p className="text-sm text-gray-400 mt-1">Cliquez sur "Créer un compte" pour commencer</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de création d'utilisateur */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">👥 Créer un compte utilisateur</h3>
              <button
                onClick={() => setShowUserForm(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleUserSubmit} className="space-y-4">
              {/* Nom d'utilisateur */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom d'utilisateur *
                </label>
                <input
                  type="text"
                  value={userFormData.username}
                  onChange={(e) => setUserFormData({...userFormData, username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="jordan.admin"
                  required
                />
              </div>

              {/* Mot de passe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Mot de passe sécurisé"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Choisissez un mot de passe fort (8+ caractères)
                </p>
              </div>

              {/* Type de compte */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de compte *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="role"
                      value="admin"
                      checked={userFormData.role === 'admin'}
                      onChange={(e) => setUserFormData({...userFormData, role: e.target.value, employee_id: ''})}
                      className="mr-2"
                    />
                    <span className="text-sm">⚡ <strong>Administrateur</strong> - Accès complet à l'interface</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="role"
                      value="employee"
                      checked={userFormData.role === 'employee'}
                      onChange={(e) => setUserFormData({...userFormData, role: e.target.value})}
                      className="mr-2"
                    />
                    <span className="text-sm">👤 <strong>Employé</strong> - Accès lecture seule à ses propres données</span>
                  </label>
                </div>
              </div>

              {/* Employé lié (si rôle employé) */}
              {userFormData.role === 'employee' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employé à associer *
                  </label>
                  <select
                    value={userFormData.employee_id}
                    onChange={(e) => setUserFormData({...userFormData, employee_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  >
                    <option value="">Sélectionnez un employé</option>
                    {employees
                      .filter(emp => !users.some(user => user.employee_id === emp.id)) // Exclure ceux qui ont déjà un compte
                      .map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.prenom} {employee.nom} - {employee.poste}
                        </option>
                      ))
                    }
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    L'employé pourra voir uniquement ses propres pointages
                  </p>
                </div>
              )}

              {/* Boutons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUserForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-400 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-blue-500 hover:to-blue-600"
                >
                  Créer le compte
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Informations importantes */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">ℹ️ Important</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Les modifications s'appliqueront immédiatement après sauvegarde</li>
          <li>• Le logo et le nom apparaîtront sur la page de connexion et dans toute l'application</li>
          <li>• La page se rechargera automatiquement pour appliquer les changements</li>
          <li>• Utilisez des images de bonne qualité pour un rendu professionnel</li>
        </ul>
      </div>
    </div>
  );
};

// NOUVEAU: Composant pour les données employé (accès limité)
const EmployeeDataTab = ({ user }) => {
  const [myData, setMyData] = useState(null);
  const [myTimeEntries, setMyTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMyData();
    loadMyTimeEntries();
  }, []);

  const loadMyData = async () => {
    try {
      const response = await axios.get(`${API}/employee/my-data`);
      setMyData(response.data);
    } catch (error) {
      console.error('Error loading my data:', error);
    }
  };

  const loadMyTimeEntries = async () => {
    try {
      const response = await axios.get(`${API}/employee/my-time-entries`);
      setMyTimeEntries(response.data);
    } catch (error) {
      console.error('Error loading my time entries:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mr-3"></div>
        <span>Chargement de vos données...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Mes Données Personnelles</h2>
        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
          👤 Employé - Accès Lecture Seule
        </span>
      </div>

      {/* Informations personnelles */}
      {myData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">👤 Mes Informations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Nom Complet</label>
              <p className="text-lg font-semibold text-gray-900">{myData.prenom} {myData.nom}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Poste</label>
              <p className="text-lg text-gray-700">{myData.poste}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Département</label>
              <p className="text-lg text-gray-700">{myData.departement}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="text-lg text-gray-700">{myData.email || 'Non renseigné'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Téléphone</label>
              <p className="text-lg text-gray-700">{myData.telephone || 'Non renseigné'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Statut Actuel</label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                myData.statut === 'Présent' ? 'bg-green-100 text-green-800' :
                myData.statut === 'En pause' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
              }`}>
                {myData.statut}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Mes pointages récents */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">⏰ Mes Pointages Récents</h3>
        {myTimeEntries.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-3xl mb-3">⏰</div>
            <p className="text-gray-500">Aucun pointage enregistré</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Heure</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {myTimeEntries.slice(0, 10).map((entry, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        entry.type_pointage === 'arrivee' ? 'bg-green-100 text-green-800' :
                        entry.type_pointage === 'depart' ? 'bg-red-100 text-red-800' :
                        entry.type_pointage === 'pause_debut' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {entry.type_pointage === 'arrivee' ? '🟢 Arrivée' :
                         entry.type_pointage === 'depart' ? '🔴 Départ' :
                         entry.type_pointage === 'pause_debut' ? '🟡 Pause Début' :
                         '🔵 Pause Fin'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(entry.timestamp).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {entry.notes || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {entry.photo_url ? (
                        <span className="text-green-600 text-sm">📸 Oui</span>
                      ) : (
                        <span className="text-gray-400 text-sm">❌ Non</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Information importante */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">ℹ️ Information</h4>
        <p className="text-sm text-blue-700">
          Vous avez un accès en <strong>lecture seule</strong> à vos données personnelles et vos pointages. 
          Pour toute modification, contactez votre administrateur.
        </p>
      </div>
    </div>
  );
};
const PointageDirectPage = () => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pointageType, setPointageType] = useState('arrivee');
  const [location, setLocation] = useState(null);
  const [locationTimestamp, setLocationTimestamp] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const employeeId = window.location.pathname.split('/').pop();

  // Vérifie si la géolocalisation a expiré (plus de 2 minutes)
  const isLocationExpired = () => {
    if (!locationTimestamp) return true;
    const now = new Date().getTime();
    const elapsed = now - locationTimestamp;
    return elapsed > 2 * 60 * 1000; // 2 minutes en millisecondes
  };

  // Capte une position GPS FRAÎCHE (anti-triche: re-vérifiée au moment du pointage)
  const getFreshLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Géolocalisation non supportée'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      }),
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

  useEffect(() => {
    loadEmployee();
    getLocationAutomatically();
  }, []);

  useEffect(() => {
    // Déterminer le type de pointage selon le statut employé
    if (employee) {
      if (employee.statut === 'Absent') {
        setPointageType('arrivee');
      } else if (employee.statut === 'Présent') {
        setPointageType('depart'); // Par défaut départ, mais l'utilisateur peut choisir pause
      } else if (employee.statut === 'En pause') {
        setPointageType('pause_fin');
      }
    }
  }, [employee]);

  const loadEmployee = async () => {
    try {
      // Utiliser l'endpoint public qui ne nécessite pas d'authentification
      const response = await axios.get(`${API}/public/employees/${employeeId}`);
      setEmployee(response.data);
    } catch (error) {
      console.error('Error loading employee:', error);
      alert('Employé non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const getLocationAutomatically = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          setLocationTimestamp(new Date().getTime());
          setLocationLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 } // maximumAge à 0 pour forcer une nouvelle demande
      );
    } else {
      setLocationLoading(false);
    }
  };

  const handlePhotoCapture = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhoto(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    try {
      // ANTI-TRICHE: re-capter une position GPS FRAÎCHE au moment exact du pointage
      let freshLoc;
      try {
        freshLoc = await getFreshLocation();
      } catch (geoErr) {
        alert("Impossible d'obtenir votre position GPS. Activez la localisation et réessayez.");
        setSubmitting(false);
        return;
      }
      setLocation(freshLoc);
      setLocationTimestamp(new Date().getTime());

      // Contrôle de précision côté client (le serveur revalide aussi)
      if (freshLoc.accuracy === undefined || freshLoc.accuracy === null || freshLoc.accuracy > 200) {
        const accTxt = (freshLoc.accuracy || freshLoc.accuracy === 0) ? `${Math.round(freshLoc.accuracy)} m` : 'inconnue';
        alert(`Précision GPS insuffisante (${accTxt}). Placez-vous en extérieur, activez le GPS haute précision et réessayez.`);
        setSubmitting(false);
        return;
      }

      // Utiliser l'endpoint public pour les pointages QR
      await axios.post(`${API}/public/time-entries`, {
        employee_id: employeeId,
        type_pointage: pointageType,
        localisation: freshLoc,
        photo_url: photo,
        notes: notes || null
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setPhoto(null);
        setNotes('');
        // Recharger les infos employé pour mettre à jour le statut
        loadEmployee();
        // Forcer une nouvelle géolocalisation pour le prochain pointage
        setLocation(null);
        setLocationTimestamp(null);
      }, 3000);
    } catch (error) {
      console.error('Error creating time entry:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.detail || 'Vous devez être dans un rayon de 100 mètres de votre lieu de travail');
      } else {
        alert('Erreur lors de l\'enregistrement du pointage');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Employé non trouvé</h1>
          <p className="text-gray-600">Vérifiez le QR code et réessayez</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-white text-3xl">✓</span>
          </div>
          <h1 className="text-2xl font-bold text-green-600 mb-4">Pointage enregistré !</h1>
          <p className="text-gray-700 mb-2">
            {employee.prenom} {employee.nom}
          </p>
          <p className="text-sm text-gray-500">
            {pointageType === 'arrivee' ? 'Arrivée' :
             pointageType === 'depart' ? 'Départ' :
             pointageType === 'pause_debut' ? 'Début pause' : 'Fin pause'} - {new Date().toLocaleTimeString()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8 mt-8">
          <Logo size="small" className="justify-center mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Pointage Personnel</h1>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-yellow-200">
            <div className="flex items-center justify-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-yellow-700 font-bold text-lg">
                  {employee.prenom.charAt(0)}{employee.nom.charAt(0)}
                </span>
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900">{employee.prenom} {employee.nom}</p>
                <p className="text-sm text-gray-600">{employee.poste}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg border border-yellow-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Type de pointage
              </label>
              
              {/* Logique de pointage selon le statut */}
              {employee.statut === 'Absent' ? (
                // Si absent, seulement arrivée possible
                <div className="p-4 bg-green-50 border-2 border-green-400 rounded-lg">
                  <div className="text-center">
                    <div className="text-3xl mb-2">🟢</div>
                    <div className="text-lg font-semibold text-green-700">Arrivée</div>
                    <div className="text-sm text-green-600">Cliquez pour pointer votre arrivée</div>
                  </div>
                </div>
              ) : (
                // Si présent ou en pause, plusieurs options
                <div className="grid grid-cols-2 gap-3">
                  {employee.statut === 'Présent' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setPointageType('depart')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          pointageType === 'depart'
                            ? 'border-red-400 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-1">🔴</div>
                          <div className="text-sm font-medium">Départ</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPointageType('pause_debut')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          pointageType === 'pause_debut'
                            ? 'border-yellow-400 bg-yellow-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-1">🟡</div>
                          <div className="text-sm font-medium">Pause</div>
                        </div>
                      </button>
                    </>
                  )}
                  
                  {employee.statut === 'En pause' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setPointageType('pause_fin')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          pointageType === 'pause_fin'
                            ? 'border-blue-400 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-1">🔵</div>
                          <div className="text-sm font-medium">Reprise</div>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPointageType('depart')}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          pointageType === 'depart'
                            ? 'border-red-400 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-1">🔴</div>
                          <div className="text-sm font-medium">Départ</div>
                        </div>
                      </button>
                    </>
                  )}
                </div>
              )}
              
              {/* Affichage du statut actuel */}
              <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                <div className="text-center text-sm">
                  <span className="text-gray-600">Statut actuel : </span>
                  <span className={`font-semibold ${
                    employee.statut === 'Présent' ? 'text-green-600' :
                    employee.statut === 'En pause' ? 'text-orange-600' :
                    'text-gray-600'
                  }`}>
                    {employee.statut}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Position GPS
              </label>
              {locationLoading ? (
                <div className="flex items-center justify-center p-4 bg-blue-50 rounded-lg">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-blue-700">Obtention de la position...</span>
                </div>
              ) : location && !isLocationExpired() ? (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center text-green-700">
                    <span className="text-xl mr-2">📍</span>
                    <span className="text-sm font-medium">Position GPS valide</span>
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    Précision: {Math.round(location.accuracy)}m
                    {locationTimestamp && (
                      <span className="ml-2">
                        • Obtenue: {new Date(locationTimestamp).toLocaleTimeString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>
              ) : location && isLocationExpired() ? (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center text-red-700">
                    <span className="text-xl mr-2">⚠️</span>
                    <span className="text-sm font-medium">Position GPS expirée</span>
                  </div>
                  <div className="text-xs text-red-600 mt-1">
                    Obtenue: {new Date(locationTimestamp).toLocaleTimeString('fr-FR')} (plus de 2 minutes)
                  </div>
                  <button
                    type="button"
                    onClick={getLocationAutomatically}
                    className="mt-2 w-full p-2 bg-red-100 border border-red-300 rounded text-red-700 hover:bg-red-200 transition-colors"
                  >
                    📍 Obtenir une nouvelle position
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={getLocationAutomatically}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 transition-colors"
                >
                  📍 Obtenir la position GPS
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photo (optionnel)
              </label>
              <input
                type="file"
                accept="image/*"
                capture="user"
                onChange={handlePhotoCapture}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              {photo && (
                <div className="mt-3 text-center">
                  <img src={photo} alt="Photo pointage" className="w-24 h-24 object-cover rounded-lg mx-auto border-2 border-yellow-400" />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optionnel)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="Commentaires..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !location || isLocationExpired()}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-4 px-6 rounded-xl hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105"
            >
              {submitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Enregistrement...
                </div>
              ) : (
                '✓ Enregistrer le pointage'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Composant principal App
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

const AppContent = () => {
  const { user, loading } = useAuth();

  // Gestion des routes pour QR codes
  const currentPath = window.location.pathname;
  const isDirectPointage = currentPath.startsWith('/pointage-direct/');

  // Si c'est un pointage direct via QR code, pas besoin d'authentification
  if (isDirectPointage) {
    return <PointageDirectPage />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Sinon, authentification normale requise
  return user ? <Dashboard /> : <LoginPage />;
};

export default App;